import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import Header from "./Components/Header";
import { AuthContext, AuthContextType, LangContext, LangContextType } from "./utilities/customHooks";
import Dashboard from "./Pages/Dashboard";
import Profile from "./Pages/Profile";
import Console from "./Pages/Dashboards/Console";
import Settings from "./Pages/Settings";
import * as T from "./utilities/frontendTypes"
import LoadingIndicator from "./Components/LoadingIndicator";
import { Toaster } from "./Components/ui/sonner";
import LoadingOverlay from "./Components/LoadingOverlay";
import { applyTheme } from "./Theme/utilities";
import baseTheme from "./Theme/base";
import darkTheme from "./Theme/dark";
import { getIsOSDarkMode, getUserLanguage, checkIsOnline } from "./utilities/helperFunctions";
import { isAdminLevelRole } from "./utilities/regionVisibility";
import Disconnected from "./Pages/Disconnected";
import { publicRoutes } from "./routes/publicRoutes";
import { adminRoutes } from "./routes/adminRoutes";
import { founderRoutes } from "./routes/founderRoutes";
import { expertRoutes } from "./routes/expertRoutes";
import { providerRoutes } from "./routes/providerRoutes";
import ErrorBoundary from "./Components/ErrorBoundary";
import QueryBoundary from "./Components/QueryBoundary";

function App () {

  const { isAuthenticated, isAuthInitialized, userRole, settings } = useContext(AuthContext) as AuthContextType; // [NOTE] This casting is intentional
  const pathname = useLocation().pathname;
  const { langState, setLang } = useContext(LangContext) as LangContextType
  const [ isOnline, setIsOnline ] = useState<boolean>(true);
  useEffect(() => {
    const handleNetworkChange = async () => {
      setIsOnline(await checkIsOnline());
    }
    handleNetworkChange();
    window.addEventListener('online', handleNetworkChange);
    window.addEventListener('offline', handleNetworkChange);

    return () => {
      window.removeEventListener('online', handleNetworkChange);
      window.removeEventListener('offline', handleNetworkChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
      const lang = isAdminLevelRole(userRole) ? "eng" : settings?.lang;
      if (lang === undefined) {
        setLang(langState)
      }
      let newLang = "eng" as T.LangState;
      switch(lang) {
        case "eng":
          newLang = "eng";
          break;
        case "fr":
          newLang = "fr";
          break;
        default: // undefined
          const userLang = getUserLanguage()
          if (userLang === "Fr-fr" || userLang === "fr" || userLang ===  "fr-fr"){
            newLang = "fr"
          } else {
            newLang = "eng";
          }
      }
      setLang(newLang)
      const themeMode = settings?.themeMode;
      let theme = baseTheme;
      switch(themeMode) {
        case "light":
          theme = baseTheme;
          break;
        case "dark":
          theme = darkTheme;
          break;
        default: // undefined
          theme = getIsOSDarkMode() ? darkTheme : baseTheme; // if the Setting object is not set yetm apply theme based on OS setting
      }
      applyTheme(theme);
  }, [settings]);


  function requireAuth(element: JSX.Element): JSX.Element {
    if (!isAuthInitialized) return <LoadingIndicator />;
    return isAuthenticated ? element : <Navigate to="/login" replace />;
  }

  function rolePermissionCheck(allowedRoles: T.UserRole[], element: JSX.Element): JSX.Element {

    if (userRole === null) {
      return <LoadingIndicator />
    }

    // if user's role is NOT allowed to access the route
    if (!allowedRoles.includes(userRole)) {
      return <Navigate to={"/unauthorized"} />
    }

    return element;
  }

  if (!isOnline) {
    return (
      <Disconnected />
    )
  }

  return (
    // [TODO]
    <ErrorBoundary>
      <div className={`flex bg-background h-full w-full items-center relative justify-start flex-col`}>
        <LoadingOverlay />
        <Toaster position="bottom-right" />
        <Routes>
          {publicRoutes()}
          <Route path="dashboard" element={ requireAuth(<Dashboard />) } >
            <Route index element={<Console />} />
            <Route path="profile" element={ <Profile /> } />
            {/* Settings sits outside the role route groups, which all wrap pages
                in QueryBoundary — it needs the same wrapper for the Personal
                tab's useSuspenseQuery (and route-contained error recovery). */}
            <Route path="settings" element={<QueryBoundary><Settings /></QueryBoundary>} />
            {adminRoutes({
              roleCheck: rolePermissionCheck
            })}
            {founderRoutes({ roleCheck: rolePermissionCheck })}
            {expertRoutes({
              roleCheck: rolePermissionCheck
            })}
            {providerRoutes({
              roleCheck: rolePermissionCheck
            })}
          </Route>
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

export default App;
